import { useState, useCallback, useMemo } from 'react';
import { SurveyDetailsItem } from '@core/api/useSurveys';
import { SurveyStatusIdEnum } from '@core/hooks/useCommonEnums';

interface UseSurveyItemSelectionProps {
    items: SurveyDetailsItem[];
    surveyStatus?: number;
}

interface SelectionState {
    selectedItems: number[];
    selectAllVisible: () => void;
    deselectAllVisible: () => void;
    addItemToSelection: (itemId: number) => void;
    removeItemFromSelection: (itemId: number) => void;
    getHeaderCheckboxState: () => { checked: boolean; indeterminate: boolean };
}

export const useSurveyItemSelection = ({
    items,
    surveyStatus,
}: UseSurveyItemSelectionProps): SelectionState => {
    const [selectedItems, setSelectedItems] = useState<number[]>([]);

    const isItemSelectable = useCallback(
        (item: SurveyDetailsItem) => {
            if (!item) return false;
            return surveyStatus === SurveyStatusIdEnum.Closed ? !item.isPublished : !item.isClosed;
        },
        [surveyStatus],
    );

    const visibleIds = useMemo(
        (): number[] => items
            .filter(isItemSelectable)
            .map((item) => item.id as number),
        [items, isItemSelectable],
    );

    const selectAllVisible = useCallback(() => {
        setSelectedItems((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }, [visibleIds]);

    const deselectAllVisible = useCallback(() => {
        setSelectedItems((prev) => prev.filter((id) => !visibleIds.includes(id)));
    }, [visibleIds]);

    const addItemToSelection = useCallback((itemId: number) => {
        setSelectedItems((prev) => [...prev, itemId]);
    }, []);

    const removeItemFromSelection = useCallback((itemId: number) => {
        setSelectedItems((prev) => prev.filter((id) => id !== itemId));
    }, []);

    const getHeaderCheckboxState = useCallback(() => {
        if (!visibleIds.length) return { checked: false, indeterminate: false };

        const selectedCount = visibleIds.filter((id) => selectedItems.includes(id)).length;
        return {
            checked: selectedCount === visibleIds.length,
            indeterminate: selectedCount > 0 && selectedCount < visibleIds.length,
        };
    }, [visibleIds, selectedItems]);

    return {
        selectedItems,
        selectAllVisible,
        deselectAllVisible,
        addItemToSelection,
        removeItemFromSelection,
        getHeaderCheckboxState,
    };
};
